import { type FC, type KeyboardEvent, type ReactNode, useId, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import styled, { css } from "styled-components"

import { componentBackgroundColor } from "~/utilities/styles"

type BaseTabsProps = {
	tabs: {
		title: string
		sticker?: string
		content: ReactNode
	}[]
	centerBar?: boolean
	className?: string
	asideComponent?: ReactNode
}

type TabsProps = BaseTabsProps & {
	selectedTabIndex: number
	setSelectedTabIndex: (value: number) => void
}
const Tabs: FC<TabsProps> = ({
	selectedTabIndex: requestedTabIndex,
	setSelectedTabIndex,
	tabs,
	centerBar = false,
	className,
	asideComponent,
}) => {
	const idPrefix = useId()
	// Keep one tab selected even if the tab list shrinks below the requested index.
	const selectedTabIndex = Math.min(Math.max(requestedTabIndex, 0), Math.max(tabs.length - 1, 0))
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

	// Arrow keys, Home and End move between tabs, following the WAI-ARIA tabs pattern.
	const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
		const lastIndex = tabs.length - 1
		if (lastIndex < 0) return
		const nextIndex =
			event.key === "ArrowRight"
				? index === lastIndex
					? 0
					: index + 1
				: event.key === "ArrowLeft"
					? index === 0
						? lastIndex
						: index - 1
					: event.key === "Home"
						? 0
						: event.key === "End"
							? lastIndex
							: null
		if (nextIndex === null) return
		event.preventDefault()
		setSelectedTabIndex(nextIndex)
		tabRefs.current[nextIndex]?.focus()
	}

	return (
		<Container className={className}>
			<TabBar $centerBar={centerBar}>
				<TabList role="tablist">
					{tabs.map(({ title, sticker }, index) => {
						const isActive = selectedTabIndex === index
						return (
							<Tab
								key={index}
								ref={element => {
									tabRefs.current[index] = element
								}}
								type="button"
								role="tab"
								id={`${idPrefix}-tab-${index}`}
								aria-selected={isActive}
								aria-controls={`${idPrefix}-panel-${index}`}
								tabIndex={isActive ? 0 : -1}
								$isActive={isActive}
								onClick={() => setSelectedTabIndex(index)}
								onKeyDown={event => handleKeyDown(event, index)}
							>
								{title}
								{sticker !== undefined && <TabSticker $isActive={isActive}>{sticker}</TabSticker>}
							</Tab>
						)
					})}
				</TabList>
				{asideComponent && (
					<>
						<TabsSpacer />
						{asideComponent}
					</>
				)}
			</TabBar>
			{tabs.map(({ content }, index) => (
				<TabContent
					key={index}
					role="tabpanel"
					id={`${idPrefix}-panel-${index}`}
					aria-labelledby={`${idPrefix}-tab-${index}`}
					hidden={selectedTabIndex !== index}
				>
					{content}
				</TabContent>
			))}
		</Container>
	)
}

export const ControlledTabs: FC<BaseTabsProps> = props => {
	const [selectedTabIndex, setSelectedTabIndex] = useState(0)

	return <Tabs selectedTabIndex={selectedTabIndex} setSelectedTabIndex={setSelectedTabIndex} {...props} />
}

type RouteTabsProps = {
	tabs: { title: string; sticker?: string; route: string; content: ReactNode }[]
	className?: string
}
export const RouteTabs: FC<RouteTabsProps> = ({ tabs, ...props }) => {
	const location = useLocation()
	const navigate = useNavigate()

	const routeIndex = tabs.findIndex(x => x.route === location.pathname)

	const setSelectedTabIndex = (index: number) => {
		navigate(tabs[index].route)
	}

	return (
		<Tabs
			tabs={tabs}
			selectedTabIndex={Math.max(routeIndex, 0)}
			setSelectedTabIndex={setSelectedTabIndex}
			{...props}
		/>
	)
}

type HashTabsProps = {
	tabs: { title: string; sticker?: string; hash: string; content: ReactNode }[]
	className?: string
}
export const HashTabs: FC<HashTabsProps> = ({ tabs, ...props }) => {
	const navigate = useNavigate()

	const location = useLocation()
	const hash = location.hash
	const selectedTabIndex = Math.max(
		tabs.findIndex(x => `#${x.hash}` === hash),
		0,
	)

	const setSelectedTabIndex = (index: number) => {
		navigate(`#${tabs[index].hash}`)
	}

	return <Tabs tabs={tabs} selectedTabIndex={selectedTabIndex} setSelectedTabIndex={setSelectedTabIndex} {...props} />
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
`

const TabBar = styled.div<{ $centerBar: boolean }>`
	background-color: ${componentBackgroundColor};
	padding: 6px;
	flex-wrap: wrap;
	gap: 4px;
	border-radius: 12px;
	display: flex;
	align-items: center;

	${props =>
		props.$centerBar &&
		css`
			align-self: center;
		`}
`

const TabList = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px;
`

const Tab = styled.button<{ $isActive: boolean }>`
	cursor: pointer;
	font-weight: 600;
	font-size: 14px;
	color: #475569;
	text-decoration: none;
	border-radius: 8px;
	padding: 10px 14px;
	transition: background-color 150ms, color 150ms;
	display: flex;
	align-items: center;
	gap: 8px;

	&:focus-visible {
		outline: 2px solid ${props => props.theme.primaryColor};
		outline-offset: 2px;
	}

	${props =>
		props.$isActive &&
		css`
			color: ${props => props.theme.primaryColor};
			background-color: ${props => props.theme.primaryColor}14;
		`}
`

const TabSticker = styled.span<{ $isActive: boolean }>`
	font-weight: 600;
	padding: 2px 10px;
	border-radius: 16px;
	transition: background-color 150ms, color 150ms;
	color: black;
	background-color: #efefef;

	${props =>
		props.$isActive &&
		css`
			color: ${props => props.theme.primaryColor};
			background-color: ${props.theme.primaryColor}30;
		`}
`

const TabsSpacer = styled.div`
	flex: 1 0 0;
`

const TabContent = styled.div`
	&[hidden] {
		display: none;
	}
`
